import { useQuery } from '@tanstack/react-query';
import { PlaceholderCard } from '@components/admin/PlaceholderCard';
import { adminPlatformApi, type PublicDirectoryInfo } from '@api/client';

interface ApiSectionProps {
  allowedOrigins: string;
  setAllowedOrigins: (v: string) => void;
  /** Si true, oculta "Orígenes permitidos" (per-centre) — superadmin no edita config de centros. */
  hidePerCentre?: boolean;
}

function PublicDirectoryCard() {
  const { data, isLoading } = useQuery<PublicDirectoryInfo>({
    queryKey: ['platform', 'public-directory-info'],
    queryFn: () => adminPlatformApi.publicDirectoryInfo(),
  });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-head">
        <span className="card-title">Endpoint público (catálogo)</span>
      </div>
      <div style={{ padding: '16px 18px', display: 'grid', gap: 14 }}>
        <div className="field">
          <label className="field-label">URL pública</label>
          <input
            className="input mono"
            readOnly
            value={isLoading ? 'Cargando…' : data?.url ?? ''}
            style={{ fontSize: 12 }}
          />
          <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
            La web de marketing consume este endpoint para listar los centros publicados.
          </p>
        </div>
        <div className="field">
          <label className="field-label">Orígenes permitidos</label>
          <textarea
            className="input mono"
            readOnly
            rows={Math.max(3, (data?.allowedOrigins.length ?? 0) + 1)}
            value={
              isLoading
                ? ''
                : (data?.allowedOrigins ?? []).join('\n')
            }
            placeholder={isLoading ? '' : 'Sin orígenes permitidos — el endpoint está bloqueado.'}
            style={{ fontSize: 12, resize: 'vertical' }}
          />
          <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
            Vacío = nadie puede leer (estricto). Para añadir/quitar dominios, edita la var
            <code style={{ margin: '0 4px', padding: '1px 4px', background: 'var(--bg-soft, #f3f4f6)', borderRadius: 3 }}>
              PUBLIC_DIRECTORY_ALLOWED_ORIGINS
            </code>
            en <code>wrangler.jsonc</code> (sección del entorno correspondiente) y redeploya.
          </p>
        </div>
      </div>
    </div>
  );
}

export function ApiSection({ allowedOrigins, setAllowedOrigins, hidePerCentre = false }: ApiSectionProps) {
  return (
    <>
      {/* Endpoint público (super only — el flag hidePerCentre es true para super) */}
      {hidePerCentre && <PublicDirectoryCard />}

      {/* API Key + Webhook URL (placeholder) */}
      <PlaceholderCard
        title="API y webhooks"
        description="Credenciales para integraciones a medida."
        preview={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="field">
              <label className="field-label">API Key</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="input mono"
                  disabled
                  value="emag_live_••••••••••••••••••••••••••"
                  style={{ flex: 1 }}
                />
                <button className="btn" disabled>Regenerar</button>
              </div>
            </div>
            <div className="field">
              <label className="field-label">Webhook URL</label>
              <input className="input mono" disabled placeholder="https://tu-servidor.com/webhook" />
              <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
                Recibe eventos POST en JSON. Documentación → equipomag.com/api
              </p>
            </div>
          </div>
        }
      />

      {/* Orígenes permitidos (real, per-centre) */}
      {!hidePerCentre && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">Orígenes permitidos (widget)</span>
          </div>
          <div style={{ padding: '16px 18px' }}>
            <textarea
              className="input mono"
              rows={4}
              style={{ resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: 12, width: '100%' }}
              placeholder={'https://tu-web.com\nhttps://otro-dominio.com'}
              value={allowedOrigins}
              onChange={(e) => { setAllowedOrigins(e.target.value); }}
            />
            <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
              Una URL por línea. El widget de reservas solo funciona embebido desde estos dominios.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
